import { useMemo } from 'react'
import { useTechnicianProfiles } from '../api/queries/useTechnicianProfiles'

/**
 * The two shift boundaries, as the profile stores them: a wall-clock time-of-day inside a datetime
 * with a dummy date part. Read the hour with `shiftHourFromIso` (`shared/lib/schedule`), never with
 * `getHours()`.
 */
export interface TechnicianShift {
  shiftStart: string
  shiftEnd: string
}

/**
 * One technician's shift, looked up by their **`User.id`**.
 *
 * `TechnicianProfile` carries two small two-digit numbers — `id` (the profile row) and `userId` (the
 * user) — and every `technicianId` in the scheduling model is the latter. Matching on `id` here
 * would silently resolve some other technician's shift, which is the trap `apps/api/CLAUDE.md`
 * calls out.
 *
 * `shift` is `null` while the query is in flight, for an id with no profile, and for an archived
 * technician — the API returns active profiles only. `isPending` is what separates the first case
 * from the other two, so a caller can wait rather than telling the user a shift is missing. A
 * disabled query is `isPending` too, which is the right answer: it has not been asked yet.
 */
export function useTechnicianShift(
  technicianUserId: number | null | undefined,
  options?: { enabled?: boolean },
): {
  shift: TechnicianShift | null
  isPending: boolean
} {
  const { data, isPending } = useTechnicianProfiles(options)

  const shift = useMemo(() => {
    if (technicianUserId == null || !data) return null
    const profile = data.find((p) => p.userId === technicianUserId)
    if (!profile) return null
    return { shiftStart: profile.shiftStart, shiftEnd: profile.shiftEnd }
  }, [data, technicianUserId])

  return { shift, isPending }
}
